import { Grid, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { reduceObj } from "../common/services/Utilities";
import { Form } from "../interfaces";
import MoreInfoButton from "./MoreInfoButton";
import clsx from "clsx";

const PREFIX = "BankBHCsToggle";

const classes = {
  button: `${PREFIX}-button`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.button}`]: {
    "width": "100%",
    "height": 40,
    "marginTop": ".5rem",
    "marginBottom": ".5rem",
    "&.focus": {
      backgroundColor: "#ffffff",
      border: "1px solid rgba(172, 200, 223, 1)",
      color: theme.palette.primary.main,
    },
    "&:hover": {
      "backgroundColor": theme.palette.primary.main,
      "border": "1px solid #ced6e3",
      "color": "#ffffff",
      "&:active": {
        backgroundColor: theme.palette.primary.main,
        color: "#ffffff",
      },
    },
  },
}));

interface Props {
  form: Form;
  setForm: (form: any) => void;
  bankValues: string[];
  bhcValues: string[];
  displayInfo?: boolean;
}

export default function BankBHCsToggle({
  form,
  setForm,
  bankValues,
  bhcValues,
  displayInfo,
}: Props) {
  const bankInstitutionValues = bankValues.map((key: string) => {
    return { key, value: "" };
  });
  const bhcFormValues = bhcValues.map((key: string) => {
    return { key, value: "" };
  });
  return (
    <Root>
      <Grid container spacing={2} sx={{ marginBottom: ".5rem" }}>
        <Grid size={6}>
          <Button
            id="banks-button"
            onClick={() =>
              setForm({
                ...form,
                institutionType: "banks",
                ...reduceObj(bhcFormValues),
              })
            }
            color="primary"
            className={clsx(
              classes.button,
              form.institutionType === "banks" && "focus",
            )}
          >
            {"Banks"}
          </Button>
        </Grid>
        <Grid size={6}>
          <Button
            id={"bhcs-button"}
            onClick={() =>
              setForm({
                ...form,
                institutionType: "bhcs",
                ...reduceObj(bankInstitutionValues),
              })
            }
            color="primary"
            className={clsx(
              classes.button,
              form.institutionType === "bhcs" && "focus",
            )}
          >
            BHCs
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={6}>
          <MoreInfoButton
            id="banks-button-info"
            popperPosition="right"
            label="Banks"
            hover
            wide={800}
            buttonStyle={{ margin: "-.5rem 0px 0rem 50px" }}
          >
            <Typography variant="h3">Banks</Typography>
            <Typography>
              This search will return results for FDIC-insured Banks and Savings
              Institutions. They include commercial banks or savings
              institutions that are state or federally chartered and are
              federally regulated by either the FDIC, the Office of the
              Comptroller of the Currency (OCC), the Federal Reserve (FED), or
              previously the Office of Thrift Suprevision (OTS).
            </Typography>
            {displayInfo && (
              <>
                <Typography>
                  The financial information appearing in Bank Find Suite is
                  obtained primarily from the Federal Financial Institution
                  Examination Council (FFIEC) Call Reports and before 2012 the
                  Office of Thrift Supervision (OTS) Thrift Financial Reports
                  submitted by all FDIC-insured depository institutions. It is
                  reported by headquarters location at the highest level of
                  consolidation.
                </Typography>
                <Typography>
                  This information is stored on and retrieved from the FDIC's
                  Research Information System (RIS) database
                </Typography>
              </>
            )}
          </MoreInfoButton>
        </Grid>
        <Grid size={6}>
          <MoreInfoButton
            id="bhcs-button-info"
            popperPosition="right"
            label="BHCs"
            hover
            wide={800}
            buttonStyle={{ margin: "-.5rem 0px 0rem 50px" }}
          >
            <Typography variant="h3">BHCs or Bank Holding Companies</Typography>
            <Typography>
              This search will return results for top regulatory bank holding
              companies and their FDIC-insured subsidiaries. The regulatory top
              holder is assigned by the Federal Reserve Board based on ownership
              and control percentages.
            </Typography>
            <Typography>
              Thrift bank holding company information is not available.
            </Typography>
            {displayInfo && (
              <Typography>
                The Financial data for bank holding companies represent the
                summation of FFIEC Call Reports or OTS Thrift Financial Reports
                (TFR) filed by all FDIC-insured bank and thrift subsidiaries
                held by a bank holding company, and do not reflect non-deposit
                subsidiaries or parent companies. Data values have not been
                adjusted for intra-company transactions. This information is
                stored on and retrieved from the FDIC's Research Information
                System (RIS) database.
              </Typography>
            )}
            <Typography>
              Official bank holding company information can be found by visiting
              the website of the Federal Reserve System - National Information
              Center.
            </Typography>
          </MoreInfoButton>
        </Grid>
      </Grid>
    </Root>
  );
}
